import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoginHeader from '../login/components/LoginHeader';
import LoginBackground from '../login/components/LoginBackground';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

// ---------------------------------------------------------------------------
// Password reset — step 1 of 2.
//
// The user enters their email; we call supabase.auth.resetPasswordForEmail,
// which sends a magic link. Clicking the link lands them on /reset-password
// with a Supabase session established, where they can pick a new password.
//
// Supabase requires the redirect URL to be in the project's "Redirect URLs"
// allowlist (Auth -> URL Configuration). Admins: see docs/ADMIN_GUIDE.md
// once that section is added.
// ---------------------------------------------------------------------------

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const supabaseLive = isSupabaseConfigured() && supabase !== null;

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!validEmail) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!supabaseLive) {
      setError(
        'Password reset requires a connected Supabase project. This app is currently in demo mode. Contact your admin.',
      );
      return;
    }

    setSubmitting(true);
    try {
      const redirectTo =
        typeof window !== 'undefined'
          ? `${window.location.origin}/reset-password`
          : undefined;

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        redirectTo ? { redirectTo } : undefined,
      );

      if (resetError) {
        // Don't leak account existence — Supabase itself typically returns
        // success even for unknown emails, but if we get a real error
        // (e.g. rate-limited), surface it.
        setError(resetError.message || 'Could not send reset email. Please try again.');
        return;
      }

      setSent(true);
    } catch (err) {
      setError(err?.message || 'Could not send reset email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <LoginHeader />

          {sent ? (
            <div className="space-y-6">
              <div className="p-4 bg-success/10 border border-success/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon
                    name="MailCheck"
                    size={20}
                    className="text-success flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="font-medium text-foreground">Check your email</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      If an account exists for <strong>{email.trim()}</strong>, we've sent
                      a password-reset link. Click the link in the email to choose a new
                      password. The link expires in about an hour.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p className="mb-2">Didn't get an email?</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Check your spam/junk folder.</li>
                  <li>Make sure the email address is spelled correctly.</li>
                  <li>Wait a minute — mail delivery is sometimes delayed.</li>
                  <li>
                    Only accounts that already exist can be reset. If you never signed
                    up, ask your admin to create an account.
                  </li>
                </ul>
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSent(false);
                    setEmail('');
                  }}
                >
                  <Icon name="RotateCcw" size={16} />
                  Try a different email
                </Button>
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  <Icon name="ArrowLeft" size={16} />
                  Back to sign in
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Reset your password
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter the email address on your EduTracker Pro account. We'll send you a
                  link to pick a new password.
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
                  <div className="flex items-start gap-2">
                    <Icon
                      name="AlertCircle"
                      size={16}
                      className="text-destructive flex-shrink-0 mt-0.5"
                    />
                    <p className="text-sm text-foreground">{error}</p>
                  </div>
                </div>
              )}

              {!supabaseLive && (
                <div className="p-3 rounded-md bg-warning/10 border border-warning/30">
                  <div className="flex items-start gap-2">
                    <Icon
                      name="Info"
                      size={16}
                      className="text-warning flex-shrink-0 mt-0.5"
                    />
                    <p className="text-sm text-foreground">
                      This app is running in <strong>Demo mode</strong> (no Supabase
                      connection). Password reset only works with a real account, so
                      you'll need to wait until an admin sets up the cloud connection.
                    </p>
                  </div>
                </div>
              )}

              <Input
                label="Email address"
                type="email"
                placeholder="you@school.edu"
                value={email}
                onChange={(e) => setEmail(e?.target?.value ?? '')}
                required
                autoComplete="email"
              />

              <Button
                type="submit"
                variant="default"
                loading={submitting}
                fullWidth
                iconName={submitting ? 'Loader2' : 'Mail'}
                iconPosition="left"
              >
                {submitting ? 'Sending reset email…' : 'Send reset link'}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1"
                >
                  <Icon name="ArrowLeft" size={14} />
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>

      <LoginBackground />
    </div>
  );
};

export default ForgotPasswordPage;
